// USPS Pub 28 Appendix C2 secondary unit designators, limited to the ones that
// are unambiguous as a unit boundary. Deliberately EXCLUDES designators that
// double as common street-name words (key, side, rear, front, lower, upper,
// slip, pier, stop) so `deriveBaseStreet` never amputates a real street like
// "100 Key Largo Blvd".
const SECONDARY_DESIGNATORS = new Set([
  "apt",
  "apartment",
  "ste",
  "suite",
  "unit",
  "fl",
  "floor",
  "rm",
  "room",
  "bldg",
  "building",
  "dept",
  "department",
  "lot",
  "spc",
  "space",
  "trlr",
  "trailer",
  "hngr",
  "hangar",
  "ph",
  "penthouse",
  "lbby",
  "lobby",
  "ofc",
  "office",
  "bsmt",
  "basement",
]);

// A real street: a house number followed by at least one name token. The base
// fallback only fires when this much survives the split.
const STREET_BASE_REGEX = /^\s*\d+\s+\S+/;

/**
 * The street portion of `addr1` that precedes an appended secondary unit, or
 * null when there's no recognizable "<street> <unit>" split.
 *
 * Why: the base-retain autocomplete path rides INCREMENTAL typing — it remembers
 * the bare street Smarty matched, then keeps autocompleting its locality after
 * the user appends a unit Smarty can't complete. A fast typist or a paste
 * collapses "<street> <unit>" into a single query that comes back empty (the
 * unit isn't a Smarty leaf), so the bare street is never queried and nothing is
 * retained — autocomplete dies even though the base street is real. Re-deriving
 * the base here lets the caller fire one fallback query for it, rebuilding the
 * same retained pool the slow-typed path would have.
 *
 * Splits at the FIRST unit designator (a "#…" token or a `SECONDARY_DESIGNATORS`
 * word), and only when a real street (number + name) precedes it — otherwise
 * returns null so the caller skips the fallback rather than query a fragment.
 */
export function deriveBaseStreet(addr1) {
  const tokens = (addr1 || "").trim().split(/\s+/);
  for (let i = 1; i < tokens.length; i++) {
    const token = tokens[i].toLowerCase();
    if (token.startsWith("#") || SECONDARY_DESIGNATORS.has(token)) {
      const base = tokens.slice(0, i).join(" ");
      return STREET_BASE_REGEX.test(base) ? base : null;
    }
  }
  return null;
}
